/**
 * HlsViewer — auto-scaling player for large-audience webinars.
 * Uses hls.js for browsers without native HLS support (all non-Safari desktop).
 * Includes chat + Q&A + poll overlay via siblings — this is video only.
 */
import { useEffect, useRef, useState } from "react";
import Hls from "hls.js";
import { LogOut, Loader2, Radio } from "lucide-react";

export default function HlsViewer({ hlsUrl, webinar, onExit }) {
  const videoRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!hlsUrl || !videoRef.current) return;
    const video = videoRef.current;

    // Safari native HLS
    if (video.canPlayType("application/vnd.apple.mpegurl")) {
      video.src = hlsUrl;
      video.addEventListener("loadedmetadata", () => setReady(true), { once: true });
      video.addEventListener("error", () => setError("Playback error"), { once: true });
      return;
    }
    // Non-Safari: use hls.js
    if (Hls.isSupported()) {
      const hls = new Hls({ enableWorker: true, lowLatencyMode: true });
      hls.loadSource(hlsUrl);
      hls.attachMedia(video);
      hls.on(Hls.Events.MANIFEST_PARSED, () => setReady(true));
      hls.on(Hls.Events.ERROR, (_evt, data) => {
        if (data.fatal) setError(`Stream error: ${data.type}`);
      });
      return () => hls.destroy();
    }
    setError("Your browser does not support HLS playback.");
  }, [hlsUrl]);

  return (
    <div className="fixed inset-0 bg-[#0B0D17] text-white flex flex-col z-50">
      <div className="flex items-center justify-between px-4 py-2 border-b border-white/5">
        <div className="flex items-center gap-3 min-w-0">
          <Radio className="w-4 h-4 text-red-500 animate-pulse" />
          <div className="min-w-0">
            <div className="text-sm font-medium truncate">{webinar?.title || "Live"}</div>
            <div className="text-[10px] text-white/50 uppercase tracking-widest">Broadcast mode</div>
          </div>
        </div>
        <button onClick={onExit} className="text-xs bg-red-500 hover:bg-red-600 rounded-full px-3 py-1.5 inline-flex items-center gap-1.5">
          <LogOut className="w-3.5 h-3.5" /> Leave
        </button>
      </div>
      <div className="flex-1 bg-black flex items-center justify-center relative">
        {!ready && !error && (
          <div className="absolute inset-0 flex items-center justify-center text-white/60">
            <Loader2 className="w-6 h-6 animate-spin" />
            <span className="ml-2 text-sm">Connecting to broadcast…</span>
          </div>
        )}
        {error && <div className="text-red-400 text-sm p-4">{error}</div>}
        <video
          ref={videoRef}
          controls
          autoPlay
          playsInline
          className="w-full h-full object-contain"
          data-testid="hls-video"
        />
      </div>
    </div>
  );
}
